import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import viewIcon from "../assets/Images/View.png";
import editIcon from "../assets/Images/Edit.png";
import deleteIcon from "../assets/icons/deleteIcon.png";
import changeStatusIcon from "../assets/Images/ChangeStatus.png";
import addIcon from "../assets/Images/AddIcon.png";
import downloadIcon from "../assets/Images/Download.png";
import filterIcon from "../assets/Images/Filter.png";
import crossIcon from "../assets/icons/crossIcon.png";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import usersIcon from "../assets/icons/users.png";
import conversion from "../assets/icons/conversation.png";
import modelsIcon from "../assets/icons/modelsvg.png";
import { useDispatch } from "react-redux";
import { setSelectedMenu } from "../redux/sidebarSlice";
import { triggerRefresh } from "../redux/refreshSlice";

const BASE_URL = `${process.env.REACT_APP_API_URL}/users`;
const ENTERPRISE_URL = `${process.env.REACT_APP_API_URL}/enterprises`;
const ROLE_URL = `${process.env.REACT_APP_API_URL}/roles`;
const DEPARTMENT_URL = `${process.env.REACT_APP_API_URL}/departments`;
const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [viewMode, setViewMode] = useState(false);
  const [roleList, setRoleList] = useState([]);
  const [userRoleName, setUserRoleName] = useState("");
  const [userCounts, setUserCounts] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
  });
  const [adminUsersCount, setAdminUsersCount] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [regularUsersCount, setRegularUsersCount] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const [chatStats, setChatStats] = useState([]);

  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchRolesAndUser = async () => {
      try {
        const res = await api.get(`${process.env.REACT_APP_API_URL}/roles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const roles = res.data;

        setRoleList(roles);

        const currentUser = JSON.parse(
          localStorage.getItem("user") || sessionStorage.getItem("user")
        );

        const userRole = roles.find((r) => r.role_id === currentUser?.role_fid);
        if (userRole) setUserRoleName(userRole.role_name);
      } catch (err) {
        console.error("Error fetching roles or user info", err);
      }
    };

    fetchRolesAndUser();
  }, []);

  const currentUser = JSON.parse(
    localStorage.getItem("user") || sessionStorage.getItem("user")
  );

  // For when user comes via navigation (e.g., from enterprise click)
  const preselectedEnterpriseId = location.state?.enterpriseId || null;

  // Use this only when needed separately
  const currentUserEnterpriseId = currentUser?.enterprise_fid || null;

  useEffect(() => {
    if (preselectedEnterpriseId) {
      setFormData((prev) => ({
        ...prev,
        enterprise_id: preselectedEnterpriseId,
      }));
    }
  }, [preselectedEnterpriseId]);

  const [formData, setFormData] = useState({
    profilepic: null,
    name: "",
    email: "",
    password: "",
    enterprise_id: "",
    role_id: "",
    department_fid: "",
  });

  const [previewUrl, setPreviewUrl] = useState("");

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const fetchData = async () => {
    try {
      const [roleRes, enterpriseRes, departmentRes] = await Promise.all([
        api.get(ROLE_URL, { headers: { Authorization: `Bearer ${token}` } }),
        api.get(ENTERPRISE_URL, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get(DEPARTMENT_URL, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setRoles(roleRes.data);
      setEnterprises(enterpriseRes.data);
      setDepartments(departmentRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  const fetchd = async () => {
    try {
      const res = await api.get(BASE_URL, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: "all" },
      });
      setUsers(res.data.data);
    } catch (error) {
      console.error("Failed to fetch users", error);
      showErrorToast(TOAST_MESSAGES.USER.FETCH_ERROR);
    }
  };

  const fetchStats = async () => {
    try {
      const [userCountRes, chatSummaryRes, userAdminCountsRes] =
        await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/users/count/all`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_URL}/chat/summary`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_URL}/users/user-admin-counts`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

      if (!userCountRes.ok) throw new Error("Failed to fetch user counts");
      const userCountData = await userCountRes.json();
      setUserCounts(userCountData);

      if (!chatSummaryRes.ok) throw new Error("Failed to fetch chat summary");
      const chatSummaryData = await chatSummaryRes.json();
      setChatStats(chatSummaryData);

      if (!userAdminCountsRes.ok)
        throw new Error("Failed to fetch user admin counts");
      const userAdminCountsData = await userAdminCountsRes.json();

      // Correct keys based on your response
      setAdminUsersCount({
        total: userAdminCountsData.admins.total,
        active: userAdminCountsData.admins.active,
        inactive: userAdminCountsData.admins.inactive,
      });

      setRegularUsersCount({
        total: userAdminCountsData.users.total,
        active: userAdminCountsData.users.active,
        inactive: userAdminCountsData.users.inactive,
      });
    } catch (err) {
      console.error("fetchStats error:", err);
      showErrorToast(TOAST_MESSAGES.ENTERPRISE.FETCH_ERROR);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchData();
    fetchd();
  }, []);

  const filteredUsers = users
    .filter((user) => {
      if (preselectedEnterpriseId) {
        return user.enterprise_fid === preselectedEnterpriseId;
      }
      return true;
    })
    .filter(
      (user) =>
        user.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.user_email?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((user) => {
      if (!dayFilter) return true;
      const createdDate = new Date(user.created_timestamp);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dayFilter === "Today") return createdDate >= today;
      if (dayFilter === "Yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return createdDate >= yesterday && createdDate < today;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updated_timestamp);
      const dateB = new Date(b.updated_timestamp);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  const navigateToChatDetails = (user) => {
    navigate("/superadmin/user-chat-details", {
      state: { userId: user.user_id, userName: user.user_name },
    });
  };

  useEffect(() => {
    if (showForm && userRoleName === "Admin" && currentUserEnterpriseId) {
      setFormData((prev) => ({
        ...prev,
        enterprise_id: currentUserEnterpriseId,
      }));
    }
  }, [showForm, userRoleName, currentUserEnterpriseId]);

  const totalEntries = filteredUsers.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredUsers.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const resetForm = () => {
    setFormData({
      profilepic: null,
      name: "",
      email: "",
      password: "",
      enterprise_id: "",
      role_id: "",
      department_fid: "",
    });
    setPreviewUrl("");
    setEditingId(null);
    setShowForm(false);
    setConfirmPassword("");
    setViewMode(false);
  };

  const validateEmail = (email) => {
    // Basic email format: something@domain.tld (TLD 2–4 letters only)
    const basicRegex = /^[^\s@]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,4}$/;
    if (!basicRegex.test(email)) return false;

    const domain = email.split("@")[1];
    const parts = domain.split(".");

    // Allow ONLY two parts: domain + TLD (example: nextastra.com)
    return parts.length === 2;
  };

  const handleNameChange = (e) => {
    let value = e.target.value.replace(/[^\p{L}0-9 .,'_-]/gu, "");

    if (value.length > 50) return; // prevent typing beyond limit

    setFormData({ ...formData, name: value });
  };

  const nameRegex = /^(?=.*\p{L})[\p{L}0-9 .,'_-]{1,50}$/u;

  const handleSubmit1 = async (e) => {
    e.preventDefault();

    const { name, email, enterprise_id, role_id, password, department_fid } = formData;

    // ✅ Basic required field validation
    if (!enterprise_id) {
      showErrorToast(TOAST_MESSAGES.USER.REQUIRED_ENTERPRISE);
      return;
    }

    if (!role_id) {
      showErrorToast(TOAST_MESSAGES.USER.REQUIRED_ROLE);
      return;
    }

    if (!department_fid) {
      showErrorToast("Department is required.");
      return;
    }

    if (!nameRegex.test(name.trim())) {
      showErrorToast(
        "Invalid name format. Letters, numbers & . , ' _ - only allowed."
      );
      return;
    }

    if (!name.trim()) {
      showErrorToast(TOAST_MESSAGES.USER.REQUIRED_NAME);
      return;
    }

    // Strict email validation using validateEmail()
    if (!validateEmail(email)) {
      showErrorToast(TOAST_MESSAGES.USER.INVALID_EMAIL);
      return;
    }

    if (!email.trim()) {
      showErrorToast(TOAST_MESSAGES.USER.REQUIRED_EMAIL);
      return;
    }

    // ✅ Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showErrorToast(TOAST_MESSAGES.USER.INVALID_EMAIL);
      return;
    }

    // ✅ Password validation (only if creating new or changing password)
    if (!editingId || password) {
      if (!password) {
        showErrorToast(TOAST_MESSAGES.USER.REQUIRED_PASSWORD);
        return;
      }
      if (password.length < 6) {
        showErrorToast(TOAST_MESSAGES.USER.PASSWORD_LENGTH);
        return;
      }
      if (!/[A-Z]/.test(password)) {
        showErrorToast(TOAST_MESSAGES.USER.PASSWORD_CAPITAL);
        return;
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        showErrorToast(TOAST_MESSAGES.USER.PASSWORD_SPECIAL);
        return;
      }
      if (password !== confirmPassword) {
        showErrorToast(TOAST_MESSAGES.USER.PASSWORD_MISMATCH);
        return;
      }
    }

    // ✅ All validations passed
    setIsSubmitting(true);

    try {
      const data = new FormData();
      data.append("user_name", name.trim());
      data.append("user_email", email.trim());
      data.append("enterprise_fid", enterprise_id);
      data.append("role_fid", role_id);
      data.append("department_fid", department_fid);

      if (password) data.append("user_password", password);
      if (formData.profilepic instanceof File)
        data.append("user_profile_pic", formData.profilepic);

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      };

      if (editingId) {
        await api.put(`${BASE_URL}/${editingId}`, data, { headers });
        showSuccessToast("User updated successfully.");
      } else {
        await api.post(BASE_URL, data, { headers });
        showSuccessToast("User added successfully.");
      }

      resetForm();
      fetchData();
      fetchd();
      fetchStats();
      dispatch(triggerRefresh());
    } catch (err) {
      console.error("✌ Submit error:", err);

      // Email already exists (backend 400 error)
      if (
        err.response?.status === 400 &&
        err.response?.data?.message === "Email already exists"
      ) {
        showErrorToast("Email already exists. Please user a different one.");
      } else {
        showErrorToast(
          editingId
            ? TOAST_MESSAGES.USER.UPDATE_ERROR
            : TOAST_MESSAGES.USER.CREATE_ERROR
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    setFormData({
      profilepic: null,
      name: user.user_name,
      email: user.user_email,
      enterprise_id: user.enterprise_fid,
      role_id: user.role_fid,
      department_fid: user.department_fid,
      password: "",
    });
    setPreviewUrl(
      user.user_profile_pic
        ? `${SERVER_URL}/${user.user_profile_pic.replace(/\\/g, "/")}`
        : ""
    );
    setEditingId(user.user_id);
    setViewMode(false);
    setShowForm(true);
  };

  const handleView = (user) => {
    setFormData({
      profilepic: null,
      name: user.user_name,
      email: user.user_email,
      enterprise_id: user.enterprise_fid,
      role_id: user.role_fid,
      department_fid: user.department_fid,
      password: "",
    });
    setPreviewUrl(
      user.user_profile_pic
        ? `${SERVER_URL}/${user.user_profile_pic.replace(/\\/g, "/")}`
        : ""
    );
    setViewMode(true);
    setShowForm(true);
  };

  const handleStatusChange = async (user) => {
    const newStatus = user.user_status === "1" ? 0 : 1;
    const statusText = newStatus === 1 ? "Active" : "Inactive";

    try {
      await api.patch(
        `${BASE_URL}/${user.user_id}/status`,
        { user_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchd(); // Refresh the data
      fetchStats();

      showSuccessToast(`User status changed to ${statusText}`);
    } catch (err) {
      console.error("Status change error:", err);
      showErrorToast(TOAST_MESSAGES.USER.STATUS_CHANGE_ERROR);
    }
  };

  const exportExcel = () => {
    const wsData = [
      ["Name", "Email", "Enterprize", "Role", "Department"],
      ...filteredUsers.map((u) => [
        u.user_name,
        u.user_email,
        enterprises.find((ent) => ent.enterprise_id === u.enterprise_fid)
          ?.enterprise_name || "",
        roles.find((role) => role.role_id === u.role_fid)?.role_name || "",
        departments.find((dept) => dept.department_id === u.department_fid)
          ?.department_name || "",
      ]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "users.xlsx");
  };

  const handleReset = () => {
    setSortOrder("desc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const overallChatSummary =
    Array.isArray(chatStats) && chatStats.length > 0
      ? chatStats[chatStats.length - 1]
      : { totalConversations: 0, usedModelCount: 0 };

  const statsData = [
    {
      label: "Total Users",
      value: regularUsersCount.total + adminUsersCount.total || 0,
      summary: [
        { label: "Users", value: regularUsersCount.total },
        { label: "Admin", value: adminUsersCount.total },
      ],
      icon: usersIcon,
    },
    {
      label: "Users",
      value: regularUsersCount.total,
      summary: [
        { label: "Active", value: regularUsersCount.active },
        { label: "Inactive", value: regularUsersCount.inactive },
      ],
      icon: usersIcon,
    },
    {
      label: "Admins",
      value: adminUsersCount.total,
      summary: [
        { label: "Active", value: adminUsersCount.active },
        { label: "Inactive", value: adminUsersCount.inactive },
      ],
      icon: usersIcon,
    },
    {
      label: "Conversations",
      value: overallChatSummary.totalConversations,
      icon: conversion,
    },
    {
      label: "Used Models",
      value: overallChatSummary.usedModelCount,
      icon: modelsIcon,
    },
  ];

  return (
    <div className="mx-2 pt-1 h-100">
      <div className="d-flex flex-wrap gap-3 mb-2 mt-0  justify-content-evenly align-items-center">
        {statsData.map((item, index) => (
          <div key={index} className="dashboard-box p-3 rounded shadow-sm">
            <div className="d-flex align-items-center mb-1">
              {item.icon && (
                <img
                  src={item.icon}
                  alt={item.label}
                  style={{
                    width: "21px",
                    height: "21px",
                  }}
                  className="icon-image1"
                />
              )}
              <h6 className="ms-2 mb-0">{item.label}</h6>
            </div>

            <h2 className="fw-bold">
              {typeof item.value !== "undefined" ? item.value : "0"}
            </h2>

            <div className="box-summary">
              {item.summary && item.summary.length > 0 ? (
                item.summary.map((s, i) => (
                  <span key={i}>
                    {s.label}: {s.value}
                  </span>
                ))
              ) : (
                <span className="invisible">placeholder</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold refresh-prompts" style={{ fontSize: "20px" }}>
          User Management Details
        </span>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        <div className="d-flex align-items-center">
          <div className="custom-search-wrapper me-3">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search Users"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <button
            className="btn btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
            title="Download"
          >
            <img src={downloadIcon} className="icon-image" alt="Download" />
          </button>

          <div className="">
            <button
              className="icon-button filter-dropdown-toggle action-button"
              type="button"
              data-bs-toggle="dropdown"
              title="Filter"
            >
              <img src={filterIcon} className="icon-image" alt="Filter" />
            </button>

            <div
              className="dropdown-menu profile-menu-dropdown p-2"
              style={{ minWidth: "200px" }}
            >
              <h6 className="fw-bolder refresh-prompts">Time</h6>
              <hr />
              {["asc", "desc"].map((order) => (
                <div className="form-check" key={order}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sortOrder"
                    id={order}
                    value={order}
                    checked={sortOrder === order}
                    onChange={() => setSortOrder(order)}
                  />
                  <label className="form-check-label" htmlFor={order}>
                    {order === "asc" ? "Ascending" : "Descending"}
                  </label>
                </div>
              ))}

              <hr />
              <h6 className="fw-bolder refresh-promts">By Day</h6>
              <hr />
              {["Today", "Yesterday", ""].map((day) => (
                <div className="form-check" key={day || "all"}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={day || "all"}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label className="form-check-label" htmlFor={day || "all"}>
                    {day || "All"}
                  </label>
                </div>
              ))}

              <hr />
              <div className="d-flex justify-content-between mt-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={() => {
                    setCurrentPage(1);
                  }}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>

          <button
            className="btn btn-outline-secondary action-button text-dark ms-2"
            onClick={() => {
              setEditingId(null);
              setViewMode(false);
              setFormData((prev) => ({
                ...prev,
                enterprise_id: preselectedEnterpriseId || "",
              }));
              setShowForm(true);
            }}
            title="Add User"
          >
            <img src={addIcon} className="icon-image" alt="Add" />
          </button>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="text-muted fs-5">No entries found.</p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table user-table1 table-sm w-100">
              <thead>
                <tr>
                  <th>Profile</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Enterprizes</th>
                  <th>Roles</th>
                  <th>Department</th>
                  <th className="text-center">Total Conversations</th>
                  <th className="text-center">Models Used</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((user, i) => (
                  <tr key={i}>
                    <td>
                      {user.user_profile_pic ? (
                        <img
                          src={`${SERVER_URL}/${user.user_profile_pic.replace(
                            /\\/g,
                            "/"
                          )}`}
                          alt="profile"
                          style={{
                            width: 25,
                            height: 25,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div className="circle-avatar">
                          {user.user_name?.charAt(0).toUpperCase() || "U"}
                        </div>
                      )}
                    </td>
                    <td
                      className="text-truncate text-left"
                      style={{ maxWidth: "120px" }}
                    >
                      {user.user_name
                        ? user.user_name.charAt(0).toUpperCase() +
                          user.user_name.slice(1)
                        : "–"}
                    </td>
                    <td
                      className="text-truncate text-left"
                      style={{ maxWidth: "120px" }}
                    >
                      {user.user_email}
                    </td>
                    <td>
                      {enterprises.find(
                        (ent) => ent.enterprise_id === user.enterprise_fid
                      )?.enterprise_name || (
                        <span className="text-muted">Unknown</span>
                      )}
                    </td>
                    <td>
                      {roles.find((role) => role.role_id === user.role_fid)
                        ?.role_name || (
                        <span className="text-muted">Unknown</span>
                      )}
                    </td>
                    <td>
                      {departments.find(
                        (dept) => dept.department_id === user.department_fid
                      )?.department_name || (
                        <span className="text-muted">Unknown</span>
                      )}
                    </td>
                    <td className="text-center">
                      {user.totalChats > 0 ? (
                        <span
                          className="text-primary text-decoration-underline"
                          role="button"
                          onClick={() => {
                            dispatch(
                              setSelectedMenu("Conversation Management")
                            );
                            navigate("/superadmin/user-session-management", {
                              state: {
                                user_id: user.user_id,
                                user_name: user.user_name,
                              },
                            });
                          }}
                        >
                          {user.totalChats}
                        </span>
                      ) : (
                        "0"
                      )}
                    </td>

                    <td className="text-center">
                      {user.modelUsedCount > 0 ? (
                        <span
                          className="text-primary text-decoration-underline"
                          role="button"
                          onClick={() => {
                            dispatch(
                              setSelectedMenu("Conversation Managements")
                            );
                            navigate("/superadmin/user-session-management", {
                              state: {
                                user_id: user.user_id,
                                user_name: user.user_name,
                              },
                            });
                          }}
                        >
                          {user.modelUsedCount}
                        </span>
                      ) : (
                        "0"
                      )}
                    </td>

                    <td>
                      {user.user_status === "1" ? (
                        <span className="">Active</span>
                      ) : (
                        <span className="">Inactive</span>
                      )}
                    </td>
                    <td>{formatDateToUserTimeZone(user.updated_timestamp)}</td>
                    <td>
                      <button
                        className="icon-button"
                        onClick={() => handleView(user)}
                        title="View"
                      >
                        <img
                          src={viewIcon}
                          className="icon-image"
                          alt="View"
                          style={{ height: "16px", width: "18px" }}
                        />
                      </button>

                      <button
                        className="icon-button"
                        onClick={() => handleEdit(user)}
                        title="Edit"
                      >
                        <img src={editIcon} className="icon-image" alt="Edit" />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleStatusChange(user)}
                        title="Change Status"
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-footer bgcolor fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(1)}
                >
                  <i className="bi bi-chevron-double-left" />
                </button>
              </li>
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage - 1)}
                >
                  <i className="bi bi-chevron-left" />
                </button>
              </li>
              {pageNumbers.map((num) => (
                <li
                  key={num}
                  className={`page-item ${currentPage === num ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(num)}
                  >
                    {num}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage + 1)}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(totalPages)}
                >
                  <i className="bi bi-chevron-double-right" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content w-75 mx-auto">
              <form onSubmit={handleSubmit1}>
                <div className="modal-header justify-content-center">
                  <h5 className="modal-title">
                    {viewMode
                      ? "View User Details"
                      : editingId
                      ? "Update User Details"
                      : "Add User Details"}
                  </h5>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={resetForm}
                    aria-label="Close"
                  >
                    <img src={crossIcon} className="icon-image" alt="Close" />
                  </button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label required-label">
                        Enterprize
                      </label>
                      <select
                        value={formData.enterprise_id}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            enterprise_id: e.target.value,
                          })
                        }
                        className="form-select"
                        disabled={userRoleName !== "Superadmin" || viewMode}
                      >
                        <option value="">Select Enterprize</option>
                        {enterprises
                          .filter(
                            (enterprise) => enterprise.enterprise_status === "1"
                          )
                          .sort((a, b) =>
                            a.enterprise_name.localeCompare(b.enterprise_name)
                          )
                          .map((enterprise) => (
                            <option
                              key={enterprise.enterprise_id}
                              value={enterprise.enterprise_id}
                            >
                              {enterprise.enterprise_name}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label required-label">Role</label>
                      <select
                        className="form-select"
                        value={formData.role_id}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            role_id: e.target.value,
                          })
                        }
                        required
                        disabled={viewMode}
                      >
                        <option value="">Select Role</option>
                        {roles
                          .filter((role) => role.role_status === "1")
                          .filter((role) =>
                            userRoleName?.toLowerCase() === "superadmin" &&
                            !preselectedEnterpriseId
                              ? true
                              : ["admin", "user"].includes(
                                  role.role_name.toLowerCase()
                                )
                          )
                          .sort((a, b) =>
                            a.role_name.localeCompare(b.role_name)
                          )
                          .map((role) => (
                            <option key={role.role_id} value={role.role_id}>
                              {role.role_name}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label required-label">
                        Department
                      </label>
                      <select
                        className="form-select"
                        value={formData.department_fid}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            department_fid: e.target.value,
                          })
                        }
                        required
                        disabled={viewMode}
                      >
                        <option value="">Select Department</option>
                        {departments
                          .sort((a, b) =>
                            a.department_name.localeCompare(b.department_name)
                          )
                          .map((dept) => (
                            <option
                              key={dept.department_id}
                              value={dept.department_id}
                            >
                              {dept.department_name}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label required-label">Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name}
                        onChange={handleNameChange}
                        required
                        disabled={viewMode}
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label required-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        name={`email-${editingId || "new"}`}
                        value={formData.email}
                        onChange={(e) => {
                          const email = e.target.value;

                          if (validateEmail(email) || email === "") {
                            setFormData({ ...formData, email });
                          } else {
                            setFormData({ ...formData, email });
                            console.warn("Invalid email format");
                          }
                        }}
                        autoComplete="new-email"
                        required
                        disabled={viewMode}
                      />
                    </div>

                    {!viewMode && (
                      <>
                        <div className="col-md-4 mb-3">
                          <label className="form-label required-label">
                            Password
                          </label>
                          <input
                            type="password"
                            className="form-control"
                            value={formData.password}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                password: e.target.value,
                              })
                            }
                            required={!editingId}
                          />
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label required-label">
                            Confirm Password
                          </label>
                          <input
                            type="password"
                            className="form-control"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required={!editingId}
                          />
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">Profile Picture</label>
                          <input
                            type="file"
                            className="file-input-reverse"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              setFormData({
                                ...formData,
                                profilepic: file,
                              });
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setPreviewUrl(reader.result);
                                };
                                reader.readAsDataURL(file);
                              } else {
                                setPreviewUrl("");
                              }
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="cancle-btn"
                    onClick={resetForm}
                  >
                    {viewMode ? "Close" : "Cancel"}
                  </button>
                  {!viewMode && (
                    <button type="submit" className="custom-btn">
                      {editingId ? "Update" : "Submit"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;